import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { UserNotification } from '@/core/notifications/model';
import { useNotificationServiceContext } from '@/core/notifications/contexts/notification-service-context';
import { navigateApp, parseSidePanelHash } from '@/core/deep-dive/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { AgedDateFormat } from '@/core/date/export';
import { MenuList } from '@/shared/components/export';
import SubscriptionChannelDropdown from '@/core/notifications/components/settings/subscription-channel-dropdown';
import styles from './notification-inbox-entry.module.css';

const TAGS_COLUMN_WIDTH = 240;

export default function NotificationInboxEntry({
  notification,
  isMobile,
  onMarkRead,
  onMarkUnread,
  onClose,
}: {
  notification: UserNotification;
  isMobile?: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onClose?: () => void;
}) {
  const { subscriptionById, topicByKey, applicationById } = useNotificationServiceContext();
  const openWidget = useOpenWidget();

  const { payload, topicKey } = notification.notification;
  const isUnread = !notification.readAt;

  const subscriptionName =
    notification.source?.type === 'subscription'
      ? subscriptionById(notification.source.id)?.name
      : undefined;

  const topic = topicByKey(topicKey);
  const appName = topic?.applicationId ? applicationById(topic.applicationId)?.name : undefined;

  // Only the title/body zone opens the notification — no click handler on the row
  // itself, so dividers, padding, and gaps around the tags/actions columns can
  // never accidentally trigger a redirect.
  const handleOpenClick = () => {
    if (isUnread) onMarkRead();
    const link = payload.link as string | undefined;
    if (!link) return;

    let url: URL;
    try {
      url = new URL(link, window.location.origin);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin) {
      window.open(link, '_blank', 'noopener');
      return;
    }

    const { appId, widgetPath } = parseSidePanelHash(url.hash);
    if (appId) {
      onClose?.();
      openWidget(appId, widgetPath ?? undefined);
      return;
    }

    onClose?.();
    navigateApp(url.pathname + url.search + url.hash);
  };

  const badgeStyle: React.CSSProperties = {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 3,
    minHeight: 18,
    padding: '2px 8px',
    background: 'var(--bs-neutral-100)',
    color: 'var(--bs-body-color)',
  };

  const actionsBar = (
    <div className="d-flex align-items-center gap-2 flex-shrink-0">
      <span
        className="flex-shrink-0 mutedText"
        style={{ whiteSpace: 'nowrap', fontSize: 11, opacity: 0.65 }}
      >
        <AgedDateFormat date={notification.createdAt} format="h:mm a" fullFormat="MMM D" />
      </span>

      {notification.source?.type === 'subscription' && (
        <SubscriptionChannelDropdown subscriptionId={notification.source.id} />
      )}

      <MenuList
        trigger={<FontAwesomeIcon icon={faEllipsisVertical} />}
        toggleTitle="More options"
        align="right"
        elevated
        items={[
          {
            key: 'toggle-read',
            label: isUnread ? 'Mark as read' : 'Mark as unread',
            onSelect: () => (isUnread ? onMarkRead() : onMarkUnread()),
          },
        ]}
      />
    </div>
  );

  const badgeList = [appName, subscriptionName].filter((b): b is string => !!b);

  if (isMobile) {
    const mobileBadgeStyle: React.CSSProperties = {
      ...badgeStyle,
      fontSize: 11,
      minHeight: 20,
      padding: '3px 8px',
    };

    return (
      <div className={`${styles.entryRow} px-3 py-2`}>
        {/* Row 1: first badge, left · date + actions, right — vertically centered together */}
        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
          {badgeList[0] ? (
            <span className="badge" style={mobileBadgeStyle}>
              {badgeList[0]}
            </span>
          ) : (
            <span />
          )}
          {actionsBar}
        </div>

        {/* Any further badges get their own row instead of crowding row 1 */}
        {badgeList.slice(1).map((label, i) => (
          <div key={i} className="mb-1">
            <span className="badge" style={mobileBadgeStyle}>
              {label}
            </span>
          </div>
        ))}

        {/* Title, then body — the only clickable-to-open zone */}
        <div style={{ cursor: 'pointer' }} onClick={handleOpenClick}>
          <div style={{ fontSize: 14, fontWeight: 600, opacity: isUnread ? 1 : 0.6 }}>
            {payload.title}
          </div>
          {payload.body && (
            <div style={{ fontSize: 13, opacity: isUnread ? 0.7 : 0.5, marginTop: 2 }}>
              {payload.body}
            </div>
          )}
        </div>
      </div>
    );
  }

  const truncatedBadgeStyle: React.CSSProperties = {
    ...badgeStyle,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <div className={`${styles.entryRow} d-flex align-items-center gap-3 px-3 py-2`}>
      {/* Title (+ first badge) on top, body (+ second badge) below — the only clickable-to-open zone */}
      <div
        className="flex-grow-1"
        style={{ minWidth: 0, cursor: 'pointer' }}
        onClick={handleOpenClick}
      >
        <div className="d-flex align-items-center gap-2">
          <div style={{ flexShrink: 0, width: TAGS_COLUMN_WIDTH }}>
            {badgeList[0] && (
              <span className="badge" style={truncatedBadgeStyle}>
                {badgeList[0]}
              </span>
            )}
          </div>
          <span
            className="text-truncate flex-grow-1"
            style={{ minWidth: 0, fontSize: 13, fontWeight: 600, opacity: isUnread ? 1 : 0.6 }}
          >
            {payload.title}
          </span>
        </div>

        {payload.body && (
          <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
            <div style={{ flexShrink: 0, width: TAGS_COLUMN_WIDTH }}>
              {badgeList[1] && (
                <span className="badge" style={truncatedBadgeStyle}>
                  {badgeList[1]}
                </span>
              )}
            </div>
            <span
              className="text-truncate flex-grow-1"
              style={{ minWidth: 0, fontSize: 12, opacity: isUnread ? 0.7 : 0.5 }}
            >
              {payload.body}
            </span>
          </div>
        )}
      </div>

      {/* Vertically centered against the combined height of both rows above */}
      {actionsBar}
    </div>
  );
}
